import type { FunctionComponent } from 'react'
import { useState } from 'react'
import type { ActionCardData, UpdateWithAiWidgetData } from '@playbooks/action-cards-utils/src'

import { useTranslation } from '../../contexts/localization/Localization'
import generateResponses from '../../services/ai/generateResponses'
import { traverseNodes } from '../../utils/actionCardOperations'
import { processReplacements } from '../../utils/processReplacements'
import AiDisclaimer from './aiDisclaimer'
import processPromptReplacements from '../../utils/ai/processPromptReplacements'
import { type WidgetProps } from './widgetTypes'
import { applyAiResponse } from '../../services/ai/applyAiResponse'
import { usePlaybooksBackendClient } from '../../hooks/usePlaybooksBackendClient'
import type { GeneratedPlayResponse } from '../../services/playbooksBackendClient'
import { CustomButton } from '../../components/ui/CustomButton'
import { useAppContext } from '../../contexts/AppContext'

interface UpdateWithAiWidgetProps extends WidgetProps {
  widget: UpdateWithAiWidgetData
}

const minAnswerConfidence = 0.2

/**
 * Widget for updating the Action Card using AI model and an arbitrary pre-configured prompt.
 * @story User Story 9721: New Action Card Widget: Update with AI
 */
const UpdateWithAiWidget: FunctionComponent<UpdateWithAiWidgetProps> = ({ widget, context }) => {
  const { data: state, update: updateState, questionsWithCode } = useAppContext()
  // Custom hooks
  const playbooksBackendClient = usePlaybooksBackendClient()
  const { text: t } = useTranslation()

  // State
  const [isLoading, setIsLoading] = useState(false)
  const [isProcessingFinished, setIsProcessingFinished] = useState(false)
  const [isResponseApplied, setIsResponseApplied] = useState(false)

  /**
   * Handle button click event.
   */
  async function handleButtonClick() {
    // Update the UI state
    setIsLoading(true)
    setIsProcessingFinished(false)
    setIsResponseApplied(false)
    // Process replacements in the prompt
    let prompt = widget.prompt
    prompt = processReplacements(prompt, questionsWithCode) || ''
    prompt = await processPromptReplacements({
      prompt,
      actionCardsData: state,
      context,
    })
    // Send request to Playbooks Backend Client
    const generatedResponses = await generateResponses({
      playbooksBackendClient,
      prompt,
      actionCardsData: state,
    })
    console.log(`Generated responses`, generatedResponses)
    // Apply the responses to the Action Card
    const appliedResponsesCount = applyAiResponses(generatedResponses, state)

    // Update the Action Card state (applied responses, widget value, tags)
    updateState(widget, 'isAcUpdated', true)
    // Update the UI state
    setIsLoading(false)
    setIsProcessingFinished(true)
    setIsResponseApplied(appliedResponsesCount > 0)
  }

  return (
    <div>
      <CustomButton
        size="lg"
        isLoading={isLoading}
        onClick={async () => {
          if (!isLoading) await handleButtonClick()
        }}
      >
        <>{widget.buttonLabel || t('updateWithAiWidget_defaultButton')}</>
      </CustomButton>
      {isProcessingFinished && widget.isAcUpdated && (
        <div className="text-right mt-3">
          <AiDisclaimer allowReporting={true} />
        </div>
      )}
    </div>
  )
}

/**
 * Apply the AI responses to the Action Card.
 * @param responses Generated responses from the AI model
 * @param actionCard The Action Card to update
 * @returns The number of applied responses
 */
function applyAiResponses(responses: GeneratedPlayResponse[], actionCard: ActionCardData) {
  let appliedResponsesCount = 0
  traverseNodes(actionCard, 'id', (node, key) => {
    const response = responses.find(response => response.questionId === node[key])
    if (response && response.confidence >= minAnswerConfidence) {
      console.log('Response found for node', node, response)
      applyAiResponse(response, node)
      appliedResponsesCount++
    }
  })
  return appliedResponsesCount
}

export default UpdateWithAiWidget
